import {
  useCallback,
  useEffect,
  useState,
  type ComponentType,
  type FC,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  SignInPageProps,
  useAnalytics,
  useApi,
  type BackstageIdentityResponse,
} from '@backstage/core-plugin-api';
import {
  InfoCard,
  Progress,
  UserIdentity,
  type SignInProviderConfig,
} from '@backstage/core-components';
import LogoFull from '../Root/LogoFull';
import { TOP_NAV_HEIGHT } from '../TopNav';

export type AutoProviderSignInPageProps = SignInPageProps & {
  provider: SignInProviderConfig;
  /** Replaces the centered spinner + message; the top Progress bar stays visible. */
  loadingContent?: ReactNode;
  ErrorComponent?: ComponentType<{ error?: Error }>;
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
  },
  appBar: {
    backgroundColor:
      (theme.palette as { navigation?: { background?: string } }).navigation
        ?.background || '#171717',
    height: TOP_NAV_HEIGHT,
    minHeight: TOP_NAV_HEIGHT,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  toolbar: {
    minHeight: TOP_NAV_HEIGHT,
    height: TOP_NAV_HEIGHT,
    padding: theme.spacing(0, 2),
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

/**
 * Minimal chrome that matches the preferred dark TopNav instead of the teal
 * Page Header used during auth boot.
 */
function SignInShell(props: { children: ReactNode }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <AppBar position="static" className={classes.appBar} elevation={0}>
        <Toolbar className={classes.toolbar} disableGutters>
          <LogoFull />
        </Toolbar>
      </AppBar>
      {props.children}
    </div>
  );
}

/**
 * Same behavior as {@link @backstage/core-components#SignInPage} single-provider `auto` mode,
 * but keeps the loading state visible during the OAuth popup instead of switching early to the
 * sign-in card (avoids flashing the login button). There is no official prop for this upstream.
 */
export const AutoProviderSignInPage: FC<AutoProviderSignInPageProps> = ({
  provider,
  onSignInSuccess,
  loadingContent,
  ErrorComponent,
  children,
}) => {
  const authApi = useApi(provider.apiRef);
  const analytics = useAnalytics();
  const classes = useStyles();
  const [error, setError] = useState<Error | undefined>();
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  const login = useCallback(
    async (options: { checkExisting?: boolean; showPopup?: boolean }) => {
      const { checkExisting, showPopup } = options;
      try {
        let identityResponse: BackstageIdentityResponse | undefined;

        if (checkExisting) {
          identityResponse = await authApi.getBackstageIdentity({
            optional: true,
          });
        }

        if (!identityResponse && (showPopup || !errorParam)) {
          // Prefer instantPopup first so sign-in completes without the OAuthRequestDialog
          // ("Login Required …" + extra Login click), which matches post-logout and normal UX.
          // If the popup is blocked or returns nothing, fall back to getBackstageIdentity() so
          // the global OAuth dialog can still complete sign-in (e.g. some token-expiry cases).
          if (showPopup) {
            identityResponse = await authApi.getBackstageIdentity({
              instantPopup: true,
            });
          } else if (!errorParam) {
            try {
              identityResponse = await authApi.getBackstageIdentity({
                instantPopup: true,
              });
            } catch (err) {
              // Only suppress popup-blocked/rejected errors; rethrow real failures
              // (network errors, 404s, server config issues) so the actual cause is shown.
              if (!(err instanceof Error) || err.name !== 'PopupRejectedError') {
                throw err;
              }
              identityResponse = undefined;
            }
            if (!identityResponse) {
              try {
                identityResponse = await authApi.getBackstageIdentity();
              } catch (err) {
                if (
                  !(err instanceof Error) ||
                  err.name !== 'PopupRejectedError'
                ) {
                  throw err;
                }
                identityResponse = undefined;
              }
            }
          }
          if (!identityResponse) {
            throw new Error(
              `The ${provider.title} provider is not configured to support sign-in`,
            );
          }
        }

        if (!identityResponse) {
          setShowLoginPage(true);
          return;
        }

        const profile = await authApi.getProfile();
        onSignInSuccess(
          UserIdentity.create({
            identity: identityResponse.identity,
            authApi,
            profile,
          }),
        );
        analytics.captureEvent('signIn', 'success');
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setShowLoginPage(true);
      }
    },
    [analytics, authApi, errorParam, onSignInSuccess, provider.title],
  );

  useEffect(() => {
    if (errorParam) {
      setError(new Error(errorParam));
    }
    void login({ checkExisting: true });
    // Intentionally once on mount; matches @backstage/core-components SignInPage behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showLoginPage) {
    return (
      <SignInShell>
        <Box width="100%">
          <Progress />
        </Box>
        <Box
          className={classes.body}
          data-testid="auto-sign-in-loading"
        >
          {loadingContent ?? (
            <>
              <CircularProgress color="primary" />
              <Box marginTop={2}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  align="center"
                >
                  We’re getting your IDP workspace ready…
                </Typography>
              </Box>
            </>
          )}
          {children}
        </Box>
      </SignInShell>
    );
  }

  return (
    <SignInShell>
      <Box className={classes.body}>
        <Grid
          container
          justifyContent="center"
          spacing={2}
          component="ul"
          style={{ margin: 0, padding: 0, listStyle: 'none', width: '100%' }}
        >
          <Grid item component="li">
            <InfoCard
              variant="fullHeight"
              title={provider.title}
              actions={
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    setError(undefined);
                    setShowLoginPage(false);
                    void login({ showPopup: true });
                  }}
                >
                  Sign in
                </Button>
              }
            >
              <Typography variant="body1">{provider.message}</Typography>
              {error &&
                error.name !== 'PopupRejectedError' &&
                (ErrorComponent ? (
                  <ErrorComponent error={error} />
                ) : (
                  <Typography variant="body1" color="error">
                    {error.message}
                  </Typography>
                ))}
            </InfoCard>
          </Grid>
        </Grid>
      </Box>
    </SignInShell>
  );
};
